import React from "react";
import { useTranslation } from "react-i18next";
import venuesEN from "../../data/venues.en.json";
import venuesEL from "../../data/venues.el.json";
import { toShortCode } from "../../hooks/useLanguage.js";

/**
 * `.real-center-link` was an anchor in the original Webflow design — it already
 * carries `color: var(--black)` and `text-decoration: none`, so a card that has
 * a `href` in its data renders as a real link with no visual change at all.
 * Cards without one stay inert `div`s, exactly as before.
 */
const VenueGrid = () => {
  const { t, i18n } = useTranslation();
  const venuesData =
    toShortCode(i18n.resolvedLanguage || i18n.language) === "el"
      ? venuesEL
      : venuesEN;
  return (
    <section
      className="section topless m-100"
      style={{ paddingBottom: "20px" }}
    >
      <div className="container w-dyn-list">
        <div role="list" className="real-list w-dyn-items">
          {venuesData.map((venue, index) => {
            const isRightAligned = index % 2 !== 0;
            const CardShell = venue.href ? "a" : "div";
            return (
              <div key={index} role="listitem" className="real-item w-dyn-item">
                <CardShell
                  {...(venue.href ? { href: venue.href } : {})}
                  className="real-center-link w-inline-block"
                >
                  <div
                    className={`real-half-bg ${
                      isRightAligned ? "w-condition-invisible" : ""
                    }`}
                  >
                    <div
                      style={{
                        backgroundImage: `url("${venue.image}")`,
                        backgroundPosition: "100%",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "cover",
                        width: "100%",
                        height: "100%",
                        opacity: 0.14,
                      }}
                    ></div>
                  </div>
                  <div
                    className={`real-half-bg droite ${
                      !isRightAligned ? "w-condition-invisible" : ""
                    }`}
                  >
                    <div
                      style={{
                        backgroundImage: `url("${venue.image}")`,
                        backgroundPosition: "0%",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "cover",
                        width: "100%",
                        height: "100%",
                        opacity: 0.14,
                      }}
                    ></div>
                  </div>
                  <div className="rel-content-wrap">
                    <div className="real-center-frame">
                      <img
                        loading="lazy"
                        alt={venue.title}
                        src={venue.image}
                        className="real-center-im"
                      />
                    </div>
                    <div
                      className={`corner-no ${
                        isRightAligned ? "w-condition-invisible" : ""
                      }`}
                    >
                      <div className="no-txt">{venue.number}</div>
                    </div>
                    <div
                      className={`corner-no droite ${
                        !isRightAligned ? "w-condition-invisible" : ""
                      }`}
                    >
                      <div className="no-txt droite">{venue.number}</div>
                    </div>
                    <div
                      className={`real-name-div ${
                        isRightAligned ? "w-condition-invisible" : ""
                      }`}
                    >
                      <div className="side-link">
                        <div>{venue.title}</div>
                      </div>
                    </div>
                    <div
                      className={`real-name-div droite ${
                        !isRightAligned ? "w-condition-invisible" : ""
                      }`}
                    >
                      <div className="side-link droite">
                        <div>{venue.title}</div>
                      </div>
                    </div>
                    <div
                      className={`ab-button-div ${
                        isRightAligned ? "w-condition-invisible" : ""
                      }`}
                    >
                      <div
                        className="button"
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          textAlign: "left",
                        }}
                      >
                        <div
                          className="button-icon"
                          style={{ marginTop: "2px" }}
                        >
                          <img
                            src="/assets/photos/Button-Icon.svg"
                            alt={t("common.buttonIconAlt")}
                            loading="lazy"
                            className="b-icon"
                          />
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            marginLeft: "12px",
                          }}
                        >
                          <div
                            style={{ textTransform: "none", lineHeight: "1.2" }}
                          >
                            {venue.description}
                          </div>
                          {venue.extra && (
                            <div
                              style={{
                                textTransform: "none",
                                fontSize: "0.85em",
                                opacity: 0.8,
                                marginTop: "4px",
                              }}
                            >
                              {venue.extra}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`ab-button-div droite ${
                        !isRightAligned ? "w-condition-invisible" : ""
                      }`}
                    >
                      <div
                        className="button"
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          textAlign: "left",
                        }}
                      >
                        <div
                          className="button-icon"
                          style={{ marginTop: "2px" }}
                        >
                          <img
                            src="/assets/photos/Button-Icon.svg"
                            alt={t("common.buttonIconAlt")}
                            loading="lazy"
                            className="b-icon"
                          />
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            marginLeft: "12px",
                          }}
                        >
                          <div
                            style={{ textTransform: "none", lineHeight: "1.2" }}
                          >
                            {venue.description}
                          </div>
                          {venue.extra && (
                            <div
                              style={{
                                textTransform: "none",
                                fontSize: "0.85em",
                                opacity: 0.8,
                                marginTop: "4px",
                              }}
                            >
                              {venue.extra}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardShell>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default VenueGrid;
